import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { IdentityOrganisationApiClient } from 'src/app/services/api-clients/identity-organisation.api-client';
import { User } from "src/app/models/user";
import { AuthenticationService } from "src/app/services/authentication.service";
import { Router } from "@angular/router";
import { IdentityWhitelabelApiClient } from "src/app/services/api-clients/identity-whitelabel.api-client";
import { IdentitySystemApiClient } from "src/app/services/api-clients/identity-system.api-client";

/** A small model to hold userinfo - located in the service since if you use the service you want this model */
export class UserInfoModel {

  constructor(userGuid: string, fullName: string) {
    this.userGuid = userGuid;
    this.fullName = fullName;
  }
  userGuid!: string;
  fullName!: string;
}
/** A small model to hold organisation info - located in the service since if you use the service you want this model */
export class OrganisationInfoModel {
  constructor(organisationGuid: string, organisationName: string) {
    this.organisationGuid = organisationGuid;
    this.organisationName = organisationName;
  }
  organisationGuid!: string;
  organisationName!: string;
}
/** A small model to hold organisation info - located in the service since if you use the service you want this model */
export class WhiteLabelInfoModel {
  constructor(whiteLabelGuid: string, whiteLabelName: string) {
    this.whiteLabelGuid = whiteLabelGuid;
    this.whiteLabelName = whiteLabelName;
  }
  whiteLabelGuid!: string;
  whiteLabelName!: string;
}
/** A small model to hold organisation info - located in the service since if you use the service you want this model */
export class SystemInfoModel {
  constructor(systemGuid: string, systemName: string) {
    this.systemGuid = systemGuid;
    this.systemName = systemName;
  }
  systemGuid!: string;
  systemName!: string;
}

@Injectable({
  providedIn: 'root'
})
export class IdsForSettingsService {

  private selectedOrganisationId: string = "";
  private selectedWhiteLabelId: string = "";
  private selectedSystemId: string = "";
  private selectedUserId: string = "";

  public selectedOrganisation$: BehaviorSubject<OrganisationInfoModel> = new BehaviorSubject(new OrganisationInfoModel("", ""));
  public selectedOrganisationId$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedOrganisationName$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedWhiteLabel$: BehaviorSubject<WhiteLabelInfoModel> = new BehaviorSubject(new WhiteLabelInfoModel("", ""));
  public selectedWhiteLabelId$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedWhiteLabelName$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedSystem$: BehaviorSubject<SystemInfoModel> = new BehaviorSubject(new SystemInfoModel("", ""));
  public selectedSystemId$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedSystemName$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedUser$: BehaviorSubject<UserInfoModel> = new BehaviorSubject(new UserInfoModel("", ""));
  public selectedUserid$: BehaviorSubject<any> = new BehaviorSubject("");
  public selectedUserName$: BehaviorSubject<any> = new BehaviorSubject("");
  public currentUserId: string = "";

  /** Set the selected user to the given Info */
  public setSelectedUser(user: UserInfoModel) {
    if (user.userGuid != "" && this.selectedUserId != "") {
      this.router.navigateByUrl(this.router.url.replace(this.selectedUserId, user.userGuid));
    }
    if (this.selectedUserId == user.userGuid) {
      // NO CHANGE
      return;
    }
    this.selectedUser$.next(user);
    this.selectedUserid$.next(user.userGuid);
    this.selectedUserId = user.userGuid;
    this.selectedUserName$.next(user.fullName);
  }

  /** Set the selected organisation to the given Info */
  public setSelectedOrganisation(organisation: OrganisationInfoModel) {
    // replace the id in url with new one IF GIVEN
    if (organisation.organisationGuid != "" && this.selectedOrganisationId != "") {
      this.router.navigateByUrl(this.router.url.replace(this.selectedOrganisationId, organisation.organisationGuid));
    }
    if (this.selectedOrganisationId == organisation.organisationGuid) {
      // NO CHANGE
      return;
    }
    this.selectedOrganisation$.next(organisation);
    this.selectedOrganisationId$.next(organisation.organisationGuid);
    this.selectedOrganisationId = organisation.organisationGuid;
    this.selectedOrganisationName$.next(organisation.organisationName);
  }
  /** Set the selected whitelabel to the given Info */
  public setSelectedWhitelabel(whitelabel: WhiteLabelInfoModel) {
    if (whitelabel.whiteLabelGuid != "" && this.selectedWhiteLabelId != "") {
      this.router.navigateByUrl(this.router.url.replace(this.selectedWhiteLabelId, whitelabel.whiteLabelGuid));
    }
    this.selectedWhiteLabel$.next(whitelabel);
    this.selectedWhiteLabelId$.next(whitelabel.whiteLabelGuid);
    this.selectedWhiteLabelId = whitelabel.whiteLabelGuid;
    this.selectedWhiteLabelName$.next(whitelabel.whiteLabelName);
  }
  /** Set the selected system to the given Info */
  public setSelectedSystem(system: SystemInfoModel) {
    if (system.systemGuid != "" && this.selectedSystemId != "") {
      this.router.navigateByUrl(this.router.url.replace(this.selectedSystemId, system.systemGuid));
    }
    this.selectedSystem$.next(system);
    this.selectedSystemId$.next(system.systemGuid);
    this.selectedSystemId = system.systemGuid;
    this.selectedSystemName$.next(system.systemName);
  }


  constructor(
    private authenticationService: AuthenticationService,
    private identityOrganisationService: IdentityOrganisationApiClient,
    private identityWhiteLabelService: IdentityWhitelabelApiClient,
    private identitySystemService: IdentitySystemApiClient,
    private router: Router) {
    // initial setup:
    // my user -> 
    // my organisation -> 
    // my whitelabel
    this.authenticationService.currentUser.subscribe({
      next: (userData: User) => {
        let fullNameOfUser = userData.firstname + " " + (userData.prefix === null ? "" : userData.prefix + " ") + userData.lastname;
        this.setSelectedUser(new UserInfoModel(userData.sub, fullNameOfUser))
        if(userData.isOrganisationAdmin || userData.isWhitelabelAdmin || userData.isSystemAdmin){
          this.getInitialOrganisation(userData.organisationGuid, userData);
        }
      }
    });
  }

  private getInitialOrganisation(organisationGuid: string, userData: User) {
    this.identityOrganisationService.getOrganisation(organisationGuid).subscribe({
      next: (organisation) => {
        this.setSelectedOrganisation(new OrganisationInfoModel(organisation?.organisation?.guid, organisation?.organisation?.name));
        if(userData.isWhitelabelAdmin || userData.isSystemAdmin){
          this.getInitialWhitelabel(organisation.organisation.enterprise?.guid, userData);
        }
      }
    });
  }

  private getInitialWhitelabel(whiteLabelGuid: string, userData: User) {
    this.identityWhiteLabelService.getWhitelabel(whiteLabelGuid).subscribe({
      next: (whiteLabel) => {
        this.setSelectedWhitelabel(new WhiteLabelInfoModel(whiteLabel?.whiteLabel?.guid, whiteLabel?.whiteLabel?.name));
        if(userData.isWhitelabelAdmin || userData.isSystemAdmin){
          this.getInitialSystem(whiteLabel.whiteLabel.whitelabelProvider?.guid);
        }
      }
    });
  }

  private getInitialSystem(systemGuid: string) {
    this.identitySystemService.getSystem(systemGuid).subscribe({
      next: (system) => {
        this.setSelectedSystem(new SystemInfoModel(system?.system?.guid, system?.system?.name));
      }
    });
  }

}
